import type { EntityIdType, EntityKind } from '../entities/vocabulary'
import {
  ENTITY_KINDS,
  entityFactsForKind,
  idTypeForProperty,
} from '../vocabulary/crossService'
import { canonicalAttributeKey, type CanonicalAttributeKey } from '../entities/attributeKeys'
import {
  roleLabel,
  type TemplateEntitySlot,
  type TemplateFieldCandidate,
  type TemplateInput,
  type TemplateSchema,
} from './schema'

function normalizeSlotRole(raw: string): string {
  const s = raw
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  return s || 'entidad'
}

/** Parse a `<slot>.<property>` binding into `{ slot, property }`, or return `null`. */
export function parseEntityBind(bind: string | null | undefined): { slot: string; property: string } | null {
  if (typeof bind !== 'string') return null
  const trimmed = bind.trim()
  const dotIdx = trimmed.indexOf('.')
  if (dotIdx <= 0 || dotIdx >= trimmed.length - 1) return null
  const slot = trimmed.slice(0, dotIdx).trim()
  const property = trimmed.slice(dotIdx + 1).trim()
  return slot && property ? { slot, property } : null
}

/** Normalize a legacy fact token or flat property key into its canonical flat `<property>` key. */
export function normalizeFactProperty(rawField: string | null | undefined, idType?: EntityIdType | null): string | undefined {
  if (typeof rawField !== 'string') return undefined
  let prop = rawField.trim()
  if (!prop) return undefined
  const dotIdx = prop.lastIndexOf('.')
  if (dotIdx !== -1) prop = prop.slice(dotIdx + 1).trim()
  if (!prop) return undefined
  if (prop === 'idNumber') return idType && idType !== 'other' ? idType : 'id_number'
  if (prop.startsWith('idNumber:')) {
    const suffix = prop.slice('idNumber:'.length).trim()
    return suffix && suffix !== 'other' ? suffix : 'id_number'
  }
  if (prop.startsWith('attribute:')) return prop.slice('attribute:'.length).trim() || undefined
  if (prop.startsWith('derived:')) return prop.slice('derived:'.length).trim() || undefined
  return prop
}

/** Validate and deduplicate an untrusted array of entity slot declarations. */
export function sanitizeEntitySlots(raw: unknown): TemplateEntitySlot[] {
  if (!Array.isArray(raw)) return []
  const bySlot = new Map<string, TemplateEntitySlot>()
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const rec = item as Record<string, unknown>
    const rawSlot = typeof rec.slot === 'string' ? rec.slot : typeof rec.role === 'string' ? rec.role : ''
    const slot = rawSlot.trim()
    if (!slot || bySlot.has(slot)) continue
    const kind: EntityKind = typeof rec.kind === 'string' && (ENTITY_KINDS as readonly string[]).includes(rec.kind)
      ? (rec.kind as EntityKind) : 'person'
    const label = typeof rec.label === 'string' && rec.label.trim() ? rec.label.trim() : roleLabel(slot)
    bySlot.set(slot, { slot, kind, label })
  }
  return [...bySlot.values()]
}

/**
 * Convert legacy `party_select` candidates into an entity slot binding `name`, `idNumber`, and
 * `attribute:address` (MAR-1070 AC6).
 */
export function convertPartySelectCandidates(
  candidates: readonly TemplateFieldCandidate[],
): TemplateFieldCandidate[] {
  const out: TemplateFieldCandidate[] = []
  for (const c of candidates) {
    if (c.suggestedType !== 'party_select') {
      out.push(c)
      continue
    }
    const role = normalizeSlotRole(c.suggestedRole || c.key)
    const kind: EntityKind = c.suggestedPartyKind ?? 'person'
    const baseLabel = c.label || roleLabel(role)
    const idType: EntityIdType = c.suggestedIdType ?? (kind === 'organization' ? 'tax_id' : 'national_id')
    const idProp = idType === 'other' ? 'id_number' : idType
    out.push(
      {
        ...c,
        key: `${role}_nombre`,
        label: `Nombre (${baseLabel})`,
        suggestedType: 'text',
        suggestedTransform: 'identity',
        suggestedBind: `${role}.name`,
        suggestedRole: role,
        suggestedPartyKind: kind,
        suggestedFact: 'name',
      },
      {
        ...c,
        token: '',
        occurrences: 0,
        segmentIndexes: [],
        key: `${role}_documento`,
        label: `Documento de identidad (${baseLabel})`,
        suggestedType: 'text',
        suggestedTransform: 'identity',
        suggestedBind: `${role}.${idProp}`,
        suggestedIdType: idType,
        suggestedRole: role,
        suggestedPartyKind: kind,
        suggestedFact: 'idNumber',
      },
      {
        ...c,
        token: '',
        occurrences: 0,
        segmentIndexes: [],
        key: `${role}_direccion`,
        label: `Dirección (${baseLabel})`,
        suggestedType: 'text',
        suggestedTransform: 'identity',
        suggestedBind: `${role}.address`,
        suggestedRole: role,
        suggestedPartyKind: kind,
        suggestedFact: 'attribute:address',
      },
    )
  }
  return out
}

/**
 * Convert legacy `party_select` `TemplateInput` entries into three `text` inputs bound to the slot's
 * `name`, `idNumber`, and `attribute:address` (MAR-1070 AC6).
 */
export function convertPartySelectInputs(inputs: readonly TemplateInput[]): TemplateInput[] {
  const out: TemplateInput[] = []
  for (const inp of inputs) {
    if (inp.type !== 'party_select') {
      out.push(inp)
      continue
    }
    const role = normalizeSlotRole(inp.role || inp.key)
    const kind: EntityKind = inp.partyKind ?? 'person'
    const baseLabel = inp.label || roleLabel(role)
    const idType: EntityIdType = inp.idType ?? (kind === 'organization' ? 'tax_id' : 'national_id')
    const idProp = idType === 'other' ? 'id_number' : idType
    out.push(
      {
        key: `${role}_nombre`, label: `Nombre (${baseLabel})`, type: 'text', required: inp.required,
        ...(inp.description ? { description: inp.description } : {}),
        bind: `${role}.name`, role, partyKind: kind, field: 'name',
      },
      {
        key: `${role}_documento`, label: `Documento de identidad (${baseLabel})`, type: 'text', required: inp.required,
        bind: `${role}.${idProp}`, idType, role, partyKind: kind, field: 'idNumber',
      },
      {
        key: `${role}_direccion`, label: `Dirección (${baseLabel})`, type: 'text', required: false,
        bind: `${role}.address`, role, partyKind: kind, field: 'attribute:address',
      },
    )
  }
  return out
}

const AMBIGUOUS_SINGLE_WORD_STEMS = new Set(['estado', 'ubicacion'])

/**
 * Infer a canonical entity fact for a legacy role-assigned input whose `field` was left blank
 * (MAR-1038 AC7 & AC10).
 *
 * Requires a stripped `<slot>` affix (`_del_<slot>`, `_de_<slot>`, `_<slot>`, `<slot>_`) for
 * general facts so unprefixed keys never false-match, and validates the resolved attribute key
 * against `entityFactsForKind(kind, 'sv')` so cross-kind stems (such as `area_trabajador` on a
 * `person` or `empresa_empleador` on an `organization`) are rejected.
 */
function inferUnboundRoleFact(
  inputKey: string,
  slot: string,
  kind: EntityKind,
): CanonicalAttributeKey | undefined {
  const normKey = normalizeSlotRole(inputKey)
  const normSlot = normalizeSlotRole(slot)
  if (!normKey || !normSlot) return undefined

  const strippedStems = new Set<string>()
  for (const suffix of [`_del_${normSlot}`, `_de_${normSlot}`, `_${normSlot}`]) {
    if (normKey.endsWith(suffix) && normKey.length > suffix.length) {
      strippedStems.add(normKey.slice(0, -suffix.length))
    }
  }
  const prefix = `${normSlot}_`
  if (normKey.startsWith(prefix) && normKey.length > prefix.length) {
    strippedStems.add(normKey.slice(prefix.length))
  }

  // Document issue place/date stems are explicit multi-word tokens that may omit `_${slot}` on
  // single-person legacy templates.
  if (kind === 'person') {
    for (const candidate of [normKey, ...strippedStems]) {
      const resolved = canonicalAttributeKey(candidate)
      if (resolved === 'id_issue_place' || resolved === 'id_issue_date') return resolved
    }
  }

  if (strippedStems.size === 0) return undefined
  const allowedKeys = new Set<string>(entityFactsForKind(kind, 'sv').map(f => f.key))

  for (const stem of strippedStems) {
    if (AMBIGUOUS_SINGLE_WORD_STEMS.has(stem)) continue
    const resolved = canonicalAttributeKey(stem)
    if (resolved && allowedKeys.has(resolved)) return resolved
  }
  return undefined
}

/**
 * Pure, idempotent normalizer that upgrades legacy `{ role, partyKind, field, idType }` and
 * `party_select` `TemplateSchema` payloads into top-level `entities: TemplateEntitySlot[]` and
 * per-input `bind: "<slot>.<property>"` (MAR-1105, MAR-1038).
 */
export function normalizeTemplateSchema(raw: Partial<TemplateSchema> | null | undefined): TemplateSchema {
  const expandedInputs = convertPartySelectInputs(raw?.inputs ?? [])
  const entitiesBySlot = new Map<string, TemplateEntitySlot>()
  const explicitKindSlots = new Set<string>()
  const boundSlotProps = new Set<string>()
  for (const e of sanitizeEntitySlots(raw?.entities)) {
    entitiesBySlot.set(e.slot, e)
    explicitKindSlots.add(e.slot)
  }
  for (const inp of expandedInputs) {
    const parsedBind = parseEntityBind(inp.bind)
    const slot = parsedBind?.slot || (typeof inp.role === 'string' && inp.role.trim() ? inp.role.trim() : undefined)
    if (!slot) continue
    const explicitProp = normalizeFactProperty(parsedBind?.property ?? inp.field, inp.idType)
    if (explicitProp) boundSlotProps.add(`${slot}.${explicitProp}`)
    const hasExplicitKind = typeof inp.partyKind === 'string' && (ENTITY_KINDS as readonly string[]).includes(inp.partyKind)
    const kind: EntityKind = hasExplicitKind ? inp.partyKind! : 'person'
    const existing = entitiesBySlot.get(slot)
    if (!existing) {
      entitiesBySlot.set(slot, { slot, kind, label: roleLabel(slot) })
      if (hasExplicitKind) explicitKindSlots.add(slot)
    } else if (hasExplicitKind && !explicitKindSlots.has(slot)) {
      entitiesBySlot.set(slot, { ...existing, kind })
      explicitKindSlots.add(slot)
    }
  }
  const normalizedInputs: TemplateInput[] = expandedInputs.map((inp) => {
    const parsedBind = parseEntityBind(inp.bind)
    const slot = parsedBind?.slot || (typeof inp.role === 'string' && inp.role.trim() ? inp.role.trim() : undefined)
    const entitySlot = slot ? entitiesBySlot.get(slot) : undefined
    const resolvedKind: EntityKind = entitySlot?.kind ?? inp.partyKind ?? 'person'
    let property = normalizeFactProperty(parsedBind?.property ?? inp.field, inp.idType)
    if (slot && !property) {
      const inferred = inferUnboundRoleFact(inp.key, slot, resolvedKind)
      if (inferred && !boundSlotProps.has(`${slot}.${inferred}`)) {
        property = inferred
        boundSlotProps.add(`${slot}.${inferred}`)
      }
    }
    const resolvedIdType = inp.idType ?? (property ? idTypeForProperty(property) ?? undefined : undefined)
    const outKind = entitySlot?.kind ?? inp.partyKind
    if (slot && property) {
      return {
        ...inp, bind: `${slot}.${property}` as const, role: slot, field: property,
        ...(outKind ? { partyKind: outKind } : {}),
        ...(resolvedIdType ? { idType: resolvedIdType } : {}),
      }
    }
    return slot
      ? { ...inp, role: slot, ...(inp.partyKind ? { partyKind: inp.partyKind } : {}), ...(resolvedIdType ? { idType: resolvedIdType } : {}) }
      : inp
  })
  return {
    ...(raw ?? {}),
    entities: [...entitiesBySlot.values()],
    inputs: normalizedInputs,
    placeholders: raw?.placeholders ?? [],
    validations: raw?.validations ?? [],
    notices: raw?.notices ?? [],
  }
}
